import { createAction } from 'redux-starter-kit';
import moment from 'moment';
import { get, post } from '../../../../utility/via-fetch';
import { requestStart, requestSucceded, requestFailed } from '../../../domain/requests/RequestActions';
import { putPackages, putPackage, putCreditUsages } from '../../../domain/packages/packageActions';
import { addMessage } from '../../application/messageUiActions';
import { putPurchaseHistory } from '../../../domain/purchases/purchaseHistoryActions';

/**
 * Sets the date range to be used on the admin-console/package-sales/{package-id} screen
 */
export const putPackagePurchaseRange = createAction('PUT_PACKAGE_PURCHASE_RANGE');

/**
 * Sets the date range to be used on the credit-utilization/{package-id} screen
 */
export const putCreditHistoryRange = createAction('PUT_PACKAGE_CREDIT_RANGE');

/**
 * An action that signifies a package is being currently edited.
 * @example dispatch(editPackage(package.code))
 */
export const editPackage = createAction('EDIT_PACKAGE');

/**
 * An action that signifies a package is no longer being edited.
 * @example dispatch(cancelEditPackage())
 */
export const cancelEditPackage = createAction('CANCEL_EDIT_PACKAGE');

/*
 * An action that signifies a request to retrieve all email content currently pending email change address
 */
export const fetchAllPackages = createAction('FETCH_ALL_PACKAGES');

/**
 * An action that retrieves a users currently pending email change address
 */
export function requestAllPackages() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAllPackages.toString()));
    dispatch(fetchAllPackages(requestId));
    try {
      const allPackages = await get('/api/tokens/packages', undefined, {});
      dispatch(putPackages(allPackages));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/*
 * An action that signifies a request to retrieve the number of package sales for each package
 */
export const fetchAllPurchaseSummaries = createAction('FETCH_ALL_PURCHASE_SUMMARIES');

/*
 * A thunk to retrieve the number of package sales for each package
 */
export function requestAllPurchaseSummaries(fromDate, toDate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAllPurchaseSummaries.toString()));
    dispatch(fetchAllPurchaseSummaries(requestId));
    try {
      const allPackages = await get('/api/tokens/packages/purchase-summaries', { fromDate: fromDate || '', toDate: toDate || '' }, {});
      dispatch(putPackages(allPackages));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/*
 * An action that signifies a request to retrieve the number of credits burned for each package
 */
export const fetchAllUsageSummaries = createAction('FETCH_ALL_USAGE_SUMMARIES');

/*
 * A thunk to retrieve the number of credits burned for each package
 */
export function requestAllUsageSummaries(fromDate, toDate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAllUsageSummaries.toString()));
    dispatch(fetchAllUsageSummaries(requestId));
    try {
      const allPackages = await get('/api/tokens/packages/revenue-summaries', { fromDate: fromDate || '', toDate: toDate || '' }, {});
      dispatch(putPackages(allPackages));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * A thunk to retrieve the value of credits burned for each package
 */
export function requestNetUsageSummaries(fromDate, toDate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAllUsageSummaries.toString()));
    dispatch(fetchAllUsageSummaries(requestId));
    try {
      const allPackages = await get('/api/tokens/packages/revenue-summaries', { fromDate: fromDate || '', toDate: toDate || '', valueType: 'net' }, {});
      dispatch(putPackages(allPackages));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/*
 * An action that signifies a request to retrieve the number of credits burned for each package by the users group
 */
export const fetchMyUsageSummaries = createAction('FETCH_MY_USAGE_SUMMARIES');

/*
 * A thunk to retrieve the credits burned summary for each package by the users groups
 */
export function requestMyUsageSummaries(fromDate, toDate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchMyUsageSummaries.toString()));
    dispatch(fetchMyUsageSummaries(requestId));
    try {
      const allPackages = await get('/api/tokens/usage/my-group', { fromDate: fromDate || '', toDate: toDate || '' }, {});
      dispatch(putPackages(allPackages));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/*
 * An action that signifies a request to retrieve the number of credits burned for each package by the users group
 */
export const fetchCreditUsages = createAction('FETCH_CREDIT_USAGES');

/**
 * A thunk to retrieve the credits usage details for a package by the users groups
 */
export function requestCreditUsageHistory(packageId, fromDate, toDate) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchMyUsageSummaries.toString()));
    dispatch(fetchMyUsageSummaries(requestId));
    try {
      const creditUsages = await get(`/api/tokens/usage/my-group/${packageId}`, { fromDate: fromDate || '', toDate: toDate || '' }, {});
      dispatch(putCreditUsages({ packageId, creditUsages }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An Action marking a request to save a new package
 */
export const savePackage = createAction('SAVE_PACKAGE');

/**
 * An action that saves a new package to the database
 */
export function requestCreatePackage(newPackage) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAllPackages.toString()));
    dispatch(fetchAllPackages(requestId));
    try {
      const packageId = await post('/api/tokens/packages', undefined, newPackage);
      dispatch(putPackage({ ...newPackage, code: packageId }));
      dispatch(addMessage('success', 'itemSaved', { item: newPackage.title }));
      dispatch(cancelEditPackage());
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/**
 * An Action marking a request to save a new package
 */
export const updatePackage = createAction('UPDATE_PACKAGE');

/**
 * An action that saves a new package to the database
 */
export function requestUpdatePackage(newPackage) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(updatePackage.toString()));
    dispatch(updatePackage(requestId));
    try {
      await post(`/api/tokens/packages/${newPackage.code}`, undefined, newPackage);
      dispatch(putPackage({ ...newPackage }));
      dispatch(addMessage('success', 'itemUpdated', { item: newPackage.title }));
      dispatch(cancelEditPackage());
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/**
 * An Action marking a request to save a new package
 */
export const togglePackageActive = createAction('TOGGLE_PACKAGE');

export const cancelTogglePackage = createAction('TOGGLE_PACKAGE_END');
/**
 * An action that activates/deactivates a new package
 */
export function requestToggleActive(newPackage) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(togglePackageActive.toString()));
    dispatch(togglePackageActive(newPackage.code));
    const toggledPackage = { ...newPackage, active: !newPackage.active };
    try {
      await post(`/api/tokens/packages/${toggledPackage.code}`, undefined, toggledPackage);
      dispatch(putPackage({ ...toggledPackage }));
      dispatch(addMessage('success', 'itemUpdated', { item: toggledPackage.title }));
      dispatch(cancelTogglePackage(newPackage.code));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
      dispatch(cancelTogglePackage(newPackage.code));
    }
  };
}

export const terminatePackage = createAction('TERMINATE_PACKAGE');
/**
 * An action that immediately ends a package
 */
export function requestTerminatePackage(newPackage) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(terminatePackage.toString()));
    dispatch(terminatePackage(requestId));
    const toggledPackage = { ...newPackage, packageEndDate: moment().toISOString() };
    try {
      await post(`/api/tokens/packages/${toggledPackage.code}/terminate`, undefined, {});
      dispatch(putPackage(toggledPackage));
      dispatch(addMessage('success', 'terminated', { title: toggledPackage.title }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error)); // We failed.
    }
  };
}

/**
 * An action creator that represents retrieving the purchase history for the given package
 */
export const fetchPurchaseHistoryForPackage = createAction('FETCH_PACKAGE_PURCHASE_HISTORY');

/**
 * A thunk action creator for making a request to retrieve the specified package id in the specified range.
 * @example dispatch(requestPurchaseHistoryForPackage(packageId, packageDateRange))
 * @param {object} packageDateRange Is an object containing ISO strings of the to and from dates { fromDateString, toDateString }
 */
export function requestPurchaseHistoryForPackage(packageId, packageDateRange) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchPurchaseHistoryForPackage.toString()));
    dispatch(fetchPurchaseHistoryForPackage(requestId));
    let fromDate = null;
    let toDate = null;
    if (packageDateRange) {
      fromDate = packageDateRange.fromDateString;
      toDate = packageDateRange.toDateString;
    }

    try {
      const result = await get(`/api/tokens/packages/${packageId}/purchases`, { fromDate: fromDate || '', toDate: toDate || '' }, {});
      dispatch(putPurchaseHistory(result));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}
